import React, { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import MenuGroup from "aldoo-ra/MenuGroup"
import { usePageContext } from "vike-react/usePageContext"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import VariantReader from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"

// Define sidebar width constants
const SIDEBAR_WIDTHS = {
  collapsed: 120,    // 120px
  expanded: 200,     // 200px (default)
  expandedXl: 220,   // 220px (xl breakpoint and above)
}

const SidePanelLayout = ({
  tabs,
  onMenuItemRender,
  menuName,
  variant = "default",
  styleOverrides,
  tabResolver, // New prop for custom tab resolution
  activeMenuItemClasses, // New prop for active menu item styling
  activeIconClasses, // New prop for active icon styling
  activeTextClasses, // New prop for active text styling
  customSidebarContent, // New prop for custom sidebar content
}) => {
  const [activeTab, setActiveTab] = useState()
  const [menu, setMenu] = useState()
  const [activeMenu, setActiveMenu] = useState()
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Set default collapsed state to true if no preference exists
    LocalUserPreferences.setDefault("ui.sidePanel.isCollapsed", true)
    // Load initial state from user preferences
    return LocalUserPreferences.get("ui.sidePanel.isCollapsed")
  })
  const context = usePageContext()

  // Function to toggle collapse state and save to preferences
  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed
    setIsCollapsed(newCollapsedState)
    LocalUserPreferences.set("ui.sidePanel.isCollapsed", newCollapsedState)
  }

  // Get variant styles and merge with overrides
  const variants = VariantReader.useVariant({
    componentName: "SidePanel",
    variant,
    styleOverrides,
  })
  const {
    panel,
    panel_expanded,
    panel_collapsed,
    menu_item,
    menu_item_expanded,
    menu_item_collapsed,
    toggle,
    toggle_top_position,
    menu_item_active,
    icon_active,
    text_active,
    icon_container,
    icon_container_expanded,
    icon_container_collapsed
  } = variants

  useEffect(() => {
    //get the active tab from the url in form of:
    let tab = context && context.routeParams["*"].split("/").pop()
    if (!tab || !tabs[tab]) tab = Object.keys(tabs)[0]

    // If tabResolver is provided, use it to potentially override the tab
    if (tabResolver) {
      const resolvedTab = tabResolver(tab, context, window.location.pathname)
      if (resolvedTab) {
        tab = resolvedTab
      }
    }

    setActiveTab(tab)

    //resolve the active menu based on the active tab
    const activeMenu =
      menu &&
      menu.find((item) => {
        const url = item.content?.link?.[0]?.content?.url
        if (!url) return false
        const currentWindowURL = new URL(window.location.href)
        return url === currentWindowURL.pathname
      })
    setActiveMenu(activeMenu?.content)
  }, [context, menu, tabResolver])

  const TabComponent = tabs[activeTab || Object.keys(tabs)[0]]

  return (
    <div className="w-full mx-auto min-h-[100vh] flex flex-row bg-admin_canvas">
      {/* Left sidebar with relative positioning for the toggle */}
      <div
        className={`sidebar-panel ${panel} ${isCollapsed ? `flex flex-col items-center px-2 ${panel_collapsed}` : `px-6 ${panel_expanded}`} relative flex-shrink-0 transition-all duration-500 ease-out`}
        style={{
          width: isCollapsed
            ? `${SIDEBAR_WIDTHS.collapsed}px`
            : `${SIDEBAR_WIDTHS.expanded}px`,
        }}
      >
        {/* Add responsive width for xl breakpoint */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (min-width: 1280px) {
              .sidebar-panel {
                width: ${isCollapsed ? `${SIDEBAR_WIDTHS.collapsed}px` : `${SIDEBAR_WIDTHS.expandedXl}px`} !important;
              }
            }
          `
        }} />

        <nav className="flex flex-col">
          {customSidebarContent ? (
            customSidebarContent
          ) : (
            <MenuGroup
              name={menuName}
              renderAs="vlist"
              styleOverrides={{
                menu: isCollapsed ? "flex flex-col items-center gap-1" : "flex flex-col gap-1",
                menu_column: "border-none",
              }}
              onData={(payload) => {
                //set the available menu items
                if (!payload?.data[0]?.content) return
                setMenu(payload.data[0].content.items)
              }}
              skipRootItem
              menuItemRender={(item) => {
                const currentPath = window.location.pathname
                const itemUrl = item?.link?.[0]?.url

                // Enhanced active detection logic
                let isActive = false

                if (activeMenu && itemUrl === currentPath) {
                  // Exact URL match
                  isActive = true
                } else if (activeTab && itemUrl) {
                  // Check if this menu item corresponds to the current active tab
                  const urlParts = itemUrl.split('/')
                  const lastPart = urlParts[urlParts.length - 1]

                  // If the menu item URL ends with the active tab name, consider it active
                  if (lastPart === activeTab) {
                    isActive = true
                  }

                  // Special case: if we're on the base module URL and this is the first tab (overview)
                  const baseModulePath = urlParts.slice(0, -1).join('/')
                  if (currentPath === baseModulePath && activeTab === Object.keys(tabs)[0] && lastPart === activeTab) {
                    isActive = true
                  }
                }

                const baseItemClassName = `${menu_item} ${isCollapsed ? menu_item_collapsed : menu_item_expanded}`
                const resolvedActiveItemClasses = activeMenuItemClasses || menu_item_active || "text-admin_primary font-semibold"
                const resolvedActiveIconClasses = activeIconClasses || icon_active || "text-admin_primary"
                const resolvedActiveTextClasses = activeTextClasses || text_active || "text-admin_primary"

                const itemClassName = isActive
                  ? `${baseItemClassName} ${resolvedActiveItemClasses}`
                  : baseItemClassName
                const iconClassName = isActive
                  ? `w-full h-auto ${resolvedActiveIconClasses}`
                  : "w-full h-auto"
                const textClassName = isActive
                  ? resolvedActiveTextClasses
                  : ""

                return (
                  <a
                    href={item?.link?.[0]?.url}
                    className={`${itemClassName} block no-underline hover:no-underline`}
                  >
                    <div className={`relative ${isCollapsed
                      ? (icon_container_collapsed || icon_container || "w-6 h-6 flex-shrink-0")
                      : (icon_container_expanded || icon_container || "w-6 h-6 flex-shrink-0")
                    }`}>
                      <DynamicSVG url={item?.image} className={iconClassName} />
                      {/* Badge for collapsed state - positioned absolutely on top-right of icon */}
                      {isCollapsed && onMenuItemRender && (
                        <div className="absolute -top-1 -right-1 transition-all duration-300 ease-in-out">
                          {onMenuItemRender(item)}
                        </div>
                      )}
                    </div>
                    {isCollapsed ? (
                      <span className={`text-center leading-tight truncate transition-opacity duration-300 ease-in-out whitespace-normal ${textClassName}`}>
                        {item?.label}
                      </span>
                    ) : (
                      <span className={`flex-1 transition-opacity duration-300 ease-in-out delay-150 ${textClassName}`}>
                        {item?.label}
                      </span>
                    )}
                    {/* Badge for expanded state - shown normally at the end */}
                    <div className={`transition-all duration-300 ease-in-out ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                      {!isCollapsed && onMenuItemRender && onMenuItemRender(item)}
                    </div>
                  </a>
                )
              }}
            />
          )}
        </nav>

        {/* Toggle button - positioned to sync perfectly with panel animation */}
        <div
          className={`fixed ${toggle_top_position} z-50 sidebar-toggle`}
          style={{
            left: isCollapsed
              ? `calc(${SIDEBAR_WIDTHS.collapsed}px - 14px)`
              : `calc(${SIDEBAR_WIDTHS.expanded}px - 14px)`,
            transition: 'left 500ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Add responsive positioning for xl breakpoint */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @media (min-width: 1280px) {
                .sidebar-toggle {
                  left: ${isCollapsed
                    ? `calc(${SIDEBAR_WIDTHS.collapsed}px - 14px)`
                    : `calc(${SIDEBAR_WIDTHS.expandedXl}px - 14px)`} !important;
                }
              }
            `
          }} />
          <Button
            className={`${toggle} w-7 h-7 flex items-center justify-center border border-admin_bordered p-2 rounded-full`}
            onClick={toggleCollapse}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <div style={{ transition: 'transform 300ms ease-in-out' }}>
              {isCollapsed ? (
                <ChevronRightIcon className="w-4 h-4 stroke-[2px]" />
              ) : (
                <ChevronLeftIcon className="w-4 h-4 stroke-[2px]" />
              )}
            </div>
          </Button>
        </div>
      </div>

      {/* Right content area */}
      <div className="flex-1 min-w-0">
        <TabComponent />
      </div>
    </div>
  )
}

export default SidePanelLayout